import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { ordersAPI, Order } from '../../services/api';
import { 
  TruckIcon, 
  CurrencyDollarIcon, 
  ClockIcon, 
  CheckCircleIcon,
  MapPinIcon,
  StarIcon,
  ChartBarIcon,
  BoltIcon,
  Cog6ToothIcon,
  BellIcon,
  DocumentTextIcon,
  ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import LocationTracker from '../Rider/LocationTracker';

const RiderDashboard: React.FC = () => {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [availableJobs, setAvailableJobs] = useState<Order[]>([]);
  const [myJobs, setMyJobs] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    todayEarnings: 0,
    totalJobs: 0,
    completedToday: 0,
    activeJobs: 0,
    rating: 5.0,
    distanceToday: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const ordersData = await ordersAPI.getUserOrders();
        
        // Filter available jobs (pending orders not assigned to anyone)
        const available = (ordersData || []).filter((order: any) => 
          order.status === 'pending' && !order.rider_id
        );
        
        // Filter my jobs (orders assigned to this rider)
        const myOrders = (ordersData || []).filter((order: any) => 
          order.rider_id === user?.id
        );
        
        // Calculate stats
        const completedToday = myOrders.filter((order: any) => 
          order.status === 'delivered' && isToday(new Date(order.created_at))
        );
        
        const todayEarnings = completedToday.reduce((sum: number, order: any) => 
          sum + Number(order.total_price ?? 0), 0
        );
        
        const activeJobs = myOrders.filter((order: any) => 
          ['accepted', 'assigned', 'picked_up', 'in_transit'].includes(order.status)
        ).length;
        
        setStats({
          todayEarnings,
          totalJobs: myOrders.length,
          completedToday: completedToday.length,
          activeJobs,
          rating: user?.average_rating || 5.0,
          distanceToday: completedToday.length * 5.2 // Mock calculation
        });
        
        setAvailableJobs(available.slice(0, 5));
        setMyJobs(myOrders.slice(0, 5));
      } catch (error) {
        console.error('Error fetching rider data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const handleAcceptJob = async (orderId: string) => {
    try {
      await ordersAPI.acceptOrder(orderId);
      // Refresh data after accepting job
      const ordersData = await ordersAPI.getUserOrders();
      const available = (ordersData || []).filter((order: any) => 
        order.status === 'pending' && !order.rider_id
      );
      const myOrders = (ordersData || []).filter((order: any) => 
        order.rider_id === user?.id
      );
      setAvailableJobs(available.slice(0, 5));
      setMyJobs(myOrders.slice(0, 5));
    } catch (error) {
      console.error('Error accepting job:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'accepted':
      case 'assigned':
        return <TruckIcon className="h-5 w-5 text-volexx-blue-500" />;
      case 'picked_up':
      case 'in_transit':
        return <TruckIcon className="h-5 w-5 text-volexx-orange-500" />;
      case 'delivered':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
      case 'assigned':
        return 'bg-blue-100 text-blue-800';
      case 'picked_up':
      case 'in_transit':
        return 'bg-orange-100 text-orange-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-volexx-blue-600"></div>
      </div>
    );
  }

  // Check approval status
  if (user && user.approval_status !== 'approved') {
    // Rejected status
    if (user.approval_status === 'rejected') {
      return (
        <div className="min-h-screen bg-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-red-50 border border-red-200 rounded-volexx shadow-volexx p-8">
              <div className="text-center">
                <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <svg className="h-12 w-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Registration Rejected</h2>
                <p className="text-gray-700 font-medium mb-3">
                  Your dispatcher registration has been rejected.
                </p>
                {user.rejection_reason && (
                  <div className="bg-white border border-red-200 rounded-volexx p-4 max-w-md mx-auto mb-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Reason for Rejection:</p>
                    <p className="text-gray-700">{user.rejection_reason}</p>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                  <Link
                    to="/edit-registration"
                    className="inline-flex items-center justify-center px-6 py-3 bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white rounded-volexx font-semibold transition-colors"
                  >
                    Edit & Resubmit Registration
                  </Link>
                </div>
                <p className="text-gray-600 text-sm max-w-md mx-auto mt-4">
                  You can update your registration details and resubmit for admin review.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Pending status (default)
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-volexx shadow-volexx p-8">
            <div className="text-center">
              <ClockIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Pending Approval</h2>
              <p className="text-gray-600 max-w-md mx-auto">
                Your dispatcher account is currently under review. You'll be able to start accepting 
                delivery jobs once your account is approved by our admin team.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Dispatcher Dashboard
          </h1>
          <p className="mt-2 text-gray-600">Welcome back, {user?.name}! Ready to earn?</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Today's Earnings */}
          <div className="bg-white rounded-volexx shadow-volexx p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Earnings</p>
                <p className="text-3xl font-bold text-volexx-blue-600 mt-2">
                  {formatPrice(stats.todayEarnings)}
                </p>
              </div>
              <div className="bg-volexx-blue-100 p-3 rounded-volexx">
                <CurrencyDollarIcon className="h-8 w-8 text-volexx-blue-600" />
              </div>
            </div>
          </div>

          {/* Active Jobs */}
          <div className="bg-white rounded-volexx shadow-volexx p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Jobs</p>
                <p className="text-3xl font-bold text-volexx-orange-600 mt-2">
                  {stats.activeJobs}
                </p>
              </div>
              <div className="bg-volexx-orange-100 p-3 rounded-volexx">
                <TruckIcon className="h-8 w-8 text-volexx-orange-600" />
              </div>
            </div>
          </div>

          {/* Completed Today */}
          <div className="bg-white rounded-volexx shadow-volexx p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed Today</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {stats.completedToday}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-volexx">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          {/* Rating */}
          <div className="bg-white rounded-volexx shadow-volexx p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Your Rating</p>
                <div className="flex items-center mt-2">
                  <p className="text-3xl font-bold text-yellow-500">{stats.rating}</p>
                  <StarIconSolid className="h-6 w-6 text-yellow-500 ml-2" />
                </div>
              </div>
              <div className="bg-yellow-100 p-3 rounded-volexx">
                <StarIcon className="h-8 w-8 text-yellow-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="bg-gradient-blue rounded-volexx shadow-volexx p-6 mb-8">
          <div className="flex items-center justify-between text-white">
            <div>
              <h3 className="text-lg font-semibold mb-2">Today's Performance</h3>
              <div className="flex items-center space-x-8">
                <div>
                  <p className="text-sm opacity-90">Distance Traveled</p>
                  <p className="text-2xl font-bold">{stats.distanceToday.toFixed(1)} km</p>
                </div>
                <div>
                  <p className="text-sm opacity-90">Available Jobs</p>
                  <p className="text-2xl font-bold">{availableJobs.length}</p>
                </div>
                <div>
                  <p className="text-sm opacity-90">Total Jobs</p>
                  <p className="text-2xl font-bold">{stats.totalJobs}</p>
                </div>
              </div>
            </div>
            <ChartBarIcon className="h-16 w-16 opacity-50" />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
          <Link
            to="/available-jobs"
            className="flex items-center p-4 bg-white border border-gray-200 rounded-volexx shadow-volexx hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-volexx-blue-100 p-3 rounded-volexx group-hover:bg-volexx-blue-200 transition-colors">
              <MapPinIcon className="h-6 w-6 text-volexx-blue-600" />
            </div>
            <div className="ml-4">
              <p className="font-semibold text-gray-900">Available Jobs</p>
              <p className="text-sm text-gray-600">Browse & accept</p>
            </div>
          </Link>

          <Link
            to="/my-jobs"
            className="flex items-center p-4 bg-white border border-gray-200 rounded-volexx shadow-volexx hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-volexx-orange-100 p-3 rounded-volexx group-hover:bg-volexx-orange-200 transition-colors">
              <TruckIcon className="h-6 w-6 text-volexx-orange-600" />
            </div>
            <div className="ml-4">
              <p className="font-semibold text-gray-900">My Jobs</p>
              <p className="text-sm text-gray-600">Track deliveries</p>
            </div>
          </Link>

          <Link
            to="/rider-notifications"
            className="flex items-center p-4 bg-white border border-gray-200 rounded-volexx shadow-volexx hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-yellow-100 p-3 rounded-volexx group-hover:bg-yellow-200 transition-colors">
              <BellIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="font-semibold text-gray-900">Notifications</p>
              <p className="text-sm text-gray-600">View updates</p>
            </div>
          </Link>

          <Link
            to="/job-history"
            className="flex items-center p-4 bg-white border border-gray-200 rounded-volexx shadow-volexx hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-indigo-100 p-3 rounded-volexx group-hover:bg-indigo-200 transition-colors">
              <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="font-semibold text-gray-900">Job History</p>
              <p className="text-sm text-gray-600">Past deliveries</p>
            </div>
          </Link>

          <Link
            to="/earnings"
            className="flex items-center p-4 bg-white border border-gray-200 rounded-volexx shadow-volexx hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-green-100 p-3 rounded-volexx group-hover:bg-green-200 transition-colors">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="font-semibold text-gray-900">Earnings</p>
              <p className="text-sm text-gray-600">View wallet</p>
            </div>
          </Link>

          <Link
            to="/support"
            className="flex items-center p-4 bg-white border border-gray-200 rounded-volexx shadow-volexx hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-pink-100 p-3 rounded-volexx group-hover:bg-pink-200 transition-colors">
              <ChatBubbleLeftEllipsisIcon className="h-6 w-6 text-pink-600" />
            </div>
            <div className="ml-4">
              <p className="font-semibold text-gray-900">Support</p>
              <p className="text-sm text-gray-600">Get help</p>
            </div>
          </Link>

          <Link
            to="/rider-profile"
            className="flex items-center p-4 bg-white border border-gray-200 rounded-volexx shadow-volexx hover:shadow-lg transition-all duration-200 group"
          >
            <div className="bg-purple-100 p-3 rounded-volexx group-hover:bg-purple-200 transition-colors">
              <Cog6ToothIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="font-semibold text-gray-900">Profile</p>
              <p className="text-sm text-gray-600">Settings</p>
            </div>
          </Link>
        </div>

        {/* Location Tracker */}
        <div className="mb-8">
          <LocationTracker autoStart={true} />
        </div>

        {/* Jobs Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Available Jobs */}
          <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">Nearby Jobs</h2>
                <Link to="/available-jobs" className="text-volexx-blue-600 hover:text-volexx-blue-700 font-medium text-sm">
                  View All
                </Link>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {availableJobs.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <BoltIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No jobs available right now</p>
                  <p className="text-sm text-gray-400 mt-1">Check back soon for new deliveries</p>
                </div>
              ) : (
                availableJobs.map((job) => (
                  <div key={job.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="px-2 py-1 bg-volexx-blue-100 text-volexx-blue-800 text-xs font-semibold rounded-volexx">
                            {(job as any).vehicle_type || 'Any Vehicle'}
                          </span>
                          <span className="text-sm font-bold text-volexx-blue-600">
                            {formatPrice(Number(job.total_price ?? 0))}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          <MapPinIcon className="h-4 w-4 inline mr-1 text-gray-400" />
                          {job.pickup_location}
                        </p>
                        <p className="text-sm text-gray-600">
                          → {job.dropoff_location}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{job.package_type}</p>
                      </div>
                      <button
                        onClick={() => handleAcceptJob(job.id)}
                        className="ml-4 bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white px-4 py-2 rounded-volexx font-semibold text-sm transition-colors"
                      >
                        Accept
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* My Active Jobs */}
          <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">My Active Jobs</h2>
                <Link
                  to="/my-jobs"
                  className="text-volexx-blue-600 hover:text-volexx-blue-700 font-medium text-sm"
                >
                  View All
                </Link>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {myJobs.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No active jobs</p>
                  <p className="text-sm text-gray-400 mt-1">Accept jobs to start earning</p>
                </div>
              ) : (
                myJobs.map((job) => (
                  <div key={job.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        {getStatusIcon(job.status)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-volexx ${getStatusBadgeColor(job.status)}`}>
                              {getStatusText(job.status)}
                            </span>
                            <span className="text-sm font-bold text-gray-900">
                              {formatPrice(Number(job.total_price ?? 0))}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {job.pickup_location}
                          </p>
                          <p className="text-sm text-gray-600">
                            → {job.dropoff_location}
                          </p>
                        </div>
                      </div>
                      <Link
                        to={`/my-jobs`}
                        className="ml-4 text-volexx-blue-600 hover:text-volexx-blue-700 font-semibold text-sm whitespace-nowrap"
                      >
                        Manage →
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiderDashboard;
